import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import BrandLogo from "../components/BrandLogo";
import { toast, Toaster } from "sonner";
import {
  Handshake, Copy, DollarSign, Users, TrendingUp, Wallet, ArrowRight, ExternalLink,
  Loader2, Banknote, Clock, Check, X as XIcon, LogOut,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Partner portal — single-page self-service dashboard for partners.
// Renders only when the logged-in user has role="partner".
// ---------------------------------------------------------------------------

export default function PartnerPortal() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [me, setMe] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b, c, d] = await Promise.all([
        api.get("/partner/me"),
        api.get("/partner/commissions"),
        api.get("/partner/referrals"),
        api.get("/partner/payout-requests"),
      ]);
      setMe(a.data); setCommissions(b.data); setReferrals(c.data); setPayouts(d.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load portal");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !me) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center text-sm text-zinc-500">
        <Loader2 className="animate-spin mr-2" size={14} /> Loading partner portal…
      </div>
    );
  }
  const p = me.partner;
  const refLink = `${window.location.origin}/register?ref=${p.referral_code}`;
  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied"); };

  return (
    <div className="min-h-screen bg-zinc-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <BrandLogo />
            <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 bg-zinc-900 text-white text-[10px] uppercase tracking-[0.2em] rounded-sm">
              <Handshake size={12} /> Partner portal
            </span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <div className="text-zinc-500 hidden sm:inline">{user?.email}</div>
            <button data-testid="partner-logout-btn" onClick={logout}
              className="text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 text-xs border border-zinc-200 px-2 py-1 rounded-sm">
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Welcome back</div>
          <h1 data-testid="partner-portal-title" className="font-display text-3xl md:text-4xl font-black tracking-tighter">{p.name}</h1>
          {p.company && <div className="text-sm text-zinc-600">{p.company}</div>}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Stat label="Lifetime earned" value={fmtUsd(me.lifetime_earned)} icon={DollarSign} accent />
          <Stat label="Available to withdraw" value={fmtUsd(me.available_balance)} icon={Wallet} />
          <Stat label="Referred users" value={me.referred_users} icon={Users} />
          <Stat label="Commissions" value={me.lifetime_commissions} icon={TrendingUp} />
        </div>

        {/* Referral block */}
        <div data-testid="partner-referral-block" className="bg-white border border-zinc-200 rounded-sm p-5 mb-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Your referral code</div>
              <div className="flex items-center gap-2">
                <code data-testid="partner-ref-code" className="text-2xl font-mono-plex font-bold bg-zinc-900 text-white px-3 py-1.5 rounded-sm">{p.referral_code}</code>
                <button onClick={() => copy(p.referral_code)} className="text-zinc-500 hover:text-zinc-900 border border-zinc-200 p-2 rounded-sm">
                  <Copy size={14} />
                </button>
              </div>
              <div className="mt-3 text-xs">
                <div className="text-zinc-500 uppercase tracking-wider mb-1">Shareable referral link</div>
                <div className="flex items-center gap-2 max-w-lg">
                  <input readOnly value={refLink} className="flex-1 border border-zinc-300 bg-zinc-50 rounded-sm px-3 py-1.5 text-xs font-mono-plex" />
                  <button data-testid="partner-copy-link" onClick={() => copy(refLink)} className="text-zinc-500 hover:text-zinc-900 border border-zinc-200 p-2 rounded-sm">
                    <Copy size={14} />
                  </button>
                </div>
              </div>
            </div>
            <div className="text-right text-sm shrink-0">
              <div className="text-xs uppercase tracking-wider text-zinc-500">Earning</div>
              <div className="font-mono-plex font-bold mt-1">
                {p.commission_type === "percent"
                  ? `${p.commission_rate}% of platform surcharge`
                  : `${fmtUsd(p.commission_rate)} per shipment`}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 border-b border-zinc-200 mb-5">
          {[
            { id: "overview", label: "Activity" },
            { id: "commissions", label: `Commissions (${commissions.length})` },
            { id: "referrals", label: `Referrals (${referrals.length})` },
            { id: "payouts", label: `Payouts (${payouts.length})` },
          ].map((t) => (
            <button key={t.id} data-testid={`partner-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${
                tab === t.id ? "border-[var(--brand-accent)] text-zinc-900 font-medium" : "border-transparent text-zinc-500 hover:text-zinc-900"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && <Activity commissions={commissions} referrals={referrals} />}
        {tab === "commissions" && <CommissionsTable rows={commissions} />}
        {tab === "referrals" && <ReferralsTable rows={referrals} />}
        {tab === "payouts" && (
          <PayoutsView payouts={payouts} available={me.available_balance} onChange={load} />
        )}

        <div className="mt-8 text-xs text-zinc-400">
          Questions? Contact your account manager or visit{" "}
          <Link to="/" className="underline inline-flex items-center gap-0.5">SellePack <ExternalLink size={10} /></Link>.
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent }) {
  const cls = accent ? "border-emerald-300 bg-emerald-50" : "border-zinc-200 bg-white";
  return (
    <div data-testid={`partner-stat-${label.toLowerCase().replace(/\s+/g,"-")}`} className={`border rounded-sm p-3 ${cls}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{label}</div>
        <Icon size={12} className={accent ? "text-emerald-600" : "text-zinc-400"} />
      </div>
      <div className="font-display text-2xl font-black tracking-tighter mt-1 truncate">{value}</div>
    </div>
  );
}

function Activity({ commissions, referrals }) {
  const recent = commissions.slice(0, 6);
  if (!commissions.length && !referrals.length) {
    return (
      <div className="bg-white border border-dashed border-zinc-300 rounded-sm p-8 text-center text-sm text-zinc-500">
        No activity yet. Share your referral link to start earning.
      </div>
    );
  }
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="bg-white border border-zinc-200 rounded-sm p-4">
        <div className="font-display font-bold mb-2">Recent commissions</div>
        {!recent.length ? <div className="text-sm text-zinc-500">No commissions yet.</div> : (
          <div className="space-y-2">
            {recent.map((c) => (
              <div key={c.commission_id} className="flex items-center justify-between text-sm border-b border-zinc-100 last:border-0 pb-2">
                <div>
                  <div className="font-mono-plex text-xs">{c.book_number}</div>
                  <div className="text-[11px] text-zinc-500">{c.created_at?.slice(0, 10)}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono-plex font-bold text-emerald-700">{fmtUsd(c.amount)}</div>
                  <div className="text-[11px] text-zinc-500">{c.commission_type === "percent" ? `${c.commission_rate}% of ${fmtUsd(c.platform_surcharge)}` : "flat"}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="bg-white border border-zinc-200 rounded-sm p-4">
        <div className="font-display font-bold mb-2">Top referrals</div>
        {!referrals.length ? <div className="text-sm text-zinc-500">No referred users yet.</div> : (
          <div className="space-y-2">
            {referrals.slice(0, 6).map((u) => (
              <div key={u.user_id} className="flex items-center justify-between text-sm border-b border-zinc-100 last:border-0 pb-2">
                <div>
                  <div>{u.name}</div>
                  <div className="text-[11px] text-zinc-500">{u.email}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono-plex font-bold">{fmtUsd(u.earned_for_partner)}</div>
                  <div className="text-[11px] text-zinc-500">{u.shipments_for_partner} ships</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CommissionsTable({ rows }) {
  if (!rows.length) return <Empty>No commissions yet.</Empty>;
  return (
    <Table head={["Date", "Book #", "Shipment surcharge", "Rate", "Earned", "Status"]}>
      {rows.map((c) => (
        <tr key={c.commission_id} className="border-b border-zinc-100">
          <td className="py-2 text-xs text-zinc-500">{c.created_at?.slice(0, 10)}</td>
          <td className="font-mono-plex text-xs">{c.book_number}</td>
          <td className="font-mono-plex text-xs">{fmtUsd(c.platform_surcharge)}</td>
          <td className="text-xs">{c.commission_type === "percent" ? `${c.commission_rate}%` : "flat"}</td>
          <td className="font-mono-plex font-bold text-emerald-700">{fmtUsd(c.amount)}</td>
          <td><span className="px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-700">{c.status}</span></td>
        </tr>
      ))}
    </Table>
  );
}

function ReferralsTable({ rows }) {
  if (!rows.length) return <Empty>No referrals yet — share your link to begin.</Empty>;
  return (
    <Table head={["User", "Email", "Company", "Status", "Shipments", "Earned for you"]}>
      {rows.map((u) => (
        <tr key={u.user_id} className="border-b border-zinc-100">
          <td className="py-2">{u.name}</td>
          <td className="text-zinc-600 text-xs">{u.email}</td>
          <td className="text-zinc-600 text-xs">{u.company || "—"}</td>
          <td><span className={`px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider ${u.approval_status === "approved" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{u.approval_status || "—"}</span></td>
          <td className="text-right font-mono-plex">{u.shipments_for_partner}</td>
          <td className="text-right font-mono-plex font-bold text-emerald-700">{fmtUsd(u.earned_for_partner)}</td>
        </tr>
      ))}
    </Table>
  );
}

function PayoutsView({ payouts, available, onChange }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("manual_ach");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const request = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a positive amount");
    setSaving(true);
    try {
      await api.post("/partner/payout-requests", { amount: amt, method, note });
      toast.success("Payout requested");
      setAmount(""); setNote("");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Request failed");
    } finally { setSaving(false); }
  };
  return (
    <div className="grid md:grid-cols-[1fr_320px] gap-5">
      <div className="bg-white border border-zinc-200 rounded-sm p-4">
        {!payouts.length ? <Empty>No payout requests yet.</Empty> : (
          <Table head={["Requested", "Amount", "Method", "Status", "Note"]}>
            {payouts.map((p) => {
              const statusBg = p.status === "paid" ? "bg-emerald-50 text-emerald-700"
                : p.status === "rejected" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700";
              const Icon = p.status === "paid" ? Check : p.status === "rejected" ? XIcon : Clock;
              return (
                <tr key={p.payout_id} className="border-b border-zinc-100">
                  <td className="py-2 text-xs text-zinc-500">{p.requested_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="font-mono-plex font-bold">{fmtUsd(p.amount)}</td>
                  <td className="text-xs">{p.method}</td>
                  <td><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider ${statusBg}`}>
                    <Icon size={10} /> {p.status}
                  </span></td>
                  <td className="text-xs text-zinc-600">{p.decision_note || p.note || "—"}</td>
                </tr>
              );
            })}
          </Table>
        )}
      </div>
      <form data-testid="partner-payout-form" onSubmit={request} className="bg-white border border-zinc-200 rounded-sm p-4 space-y-3 self-start">
        <div className="font-display font-bold">Request payout</div>
        <div className="text-xs text-zinc-500">
          Available to withdraw: <span className="font-mono-plex font-bold text-emerald-700">{fmtUsd(available)}</span>
        </div>
        <label className="block text-xs">
          <div className="uppercase tracking-wider text-zinc-500 mb-1">Amount ($)</div>
          <input data-testid="partner-payout-amount" type="number" step="0.01" min="0" value={amount}
            onChange={(e) => setAmount(e.target.value)} max={available}
            className="w-full border border-zinc-300 rounded-sm px-3 py-2 font-mono-plex" />
        </label>
        <label className="block text-xs">
          <div className="uppercase tracking-wider text-zinc-500 mb-1">Method</div>
          <select data-testid="partner-payout-method" value={method} onChange={(e) => setMethod(e.target.value)}
            className="w-full border border-zinc-300 rounded-sm px-3 py-2 bg-white text-sm">
            <option value="manual_ach">Manual ACH</option>
            <option value="stripe_connect">Stripe Connect</option>
            <option value="paypal">PayPal</option>
          </select>
        </label>
        <label className="block text-xs">
          <div className="uppercase tracking-wider text-zinc-500 mb-1">Note (optional)</div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
            className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm" />
        </label>
        <button data-testid="partner-payout-submit" type="submit" disabled={saving || !amount || parseFloat(amount) <= 0}
          className="w-full brand-bg text-white py-2 rounded-sm text-sm inline-flex items-center justify-center gap-1 disabled:opacity-50">
          {saving ? <Loader2 className="animate-spin" size={14} /> : <Banknote size={14} />} Request payout
        </button>
      </form>
    </div>
  );
}

function Table({ head, children }) {
  return (
    <div className="overflow-x-auto -mx-4 px-4">
      <table className="w-full text-sm min-w-[540px]">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
            {head.map((h, i) => <th key={i} className={i >= head.length - 2 ? "text-right py-2" : "text-left py-2"}>{h}</th>)}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Empty({ children }) {
  return <div className="bg-white border border-dashed border-zinc-300 rounded-sm p-8 text-center text-sm text-zinc-500">{children}</div>;
}

function fmtUsd(n) {
  if (n == null || isNaN(n)) return "—";
  return `$${Number(n).toFixed(2)}`;
}
